import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useGoogleLogin } from '@react-oauth/google'

interface User {
  email: string
  name: string
  picture: string
}

interface AuthenticationContextType {
  user: User | null
  isLoading: boolean
  login: () => void
  loginWithCredential: (credential: string) => void
  logout: () => void
}

const AuthenticationContext = createContext<AuthenticationContextType | undefined>(undefined)

export const useAuthentication = () => {
  const context = useContext(AuthenticationContext)
  if (!context) {
    throw new Error('useAuthentication must be used within an AuthenticationProvider')
  }
  return context
}

interface AuthenticationProviderProps {
  children: ReactNode
}

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const storedUser = localStorage.getItem('ablunch_user')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
    setIsLoading(false)
  }, [])

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${response.access_token}` }
        })
        const userData = await userInfo.json()
        
        // Check if email domain is ab180.co
        if (!userData.email.endsWith('@ab180.co')) {
          alert('AB180 직원만 로그인할 수 있습니다.')
          return
        }

        const newUser: User = {
          email: userData.email,
          name: userData.name,
          picture: userData.picture
        }
        
        setUser(newUser)
        localStorage.setItem('ablunch_user', JSON.stringify(newUser))
        localStorage.setItem('ablunch_token', response.access_token)
      } catch (error) {
        console.error('Login failed:', error)
      }
    },
    onError: (error) => {
      console.error('Login Failed:', error)
    },
    prompt: 'select_account'
  })

  const loginWithCredential = (credential: string) => {
    try {
      // ID 토큰을 디코드하여 사용자 정보 확인
      const payload = JSON.parse(atob(credential.split('.')[1]))
      
      // Check if email domain is ab180.co
      if (!payload.email.endsWith('@ab180.co')) {
        alert('AB180 직원만 로그인할 수 있습니다.')
        return
      }

      const newUser: User = {
        email: payload.email,
        name: payload.name,
        picture: payload.picture
      }
      
      setUser(newUser)
      localStorage.setItem('ablunch_user', JSON.stringify(newUser))
      localStorage.setItem('ablunch_credential', credential)
    } catch (error) {
      console.error('Login with credential failed:', error)
    }
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('ablunch_user')
    localStorage.removeItem('ablunch_token')
    localStorage.removeItem('ablunch_credential')
  }

  return (
    <AuthenticationContext.Provider value={{ user, isLoading, login, loginWithCredential, logout }}>
      {children}
    </AuthenticationContext.Provider>
  )
}